import { z } from "zod";
import {
    manualTransactionUpdateFieldsSchema,
    UpdateManualTransactionBody,
} from "@common/schemas/domains/transactions/manual-transaction.schema";
import { Transaction } from "@common/types/transaction";
import { isLockedByDateOnly } from "@helpers/lock";

export type UpdateManualTransactionFormValues = z.infer<
    typeof manualTransactionUpdateFieldsSchema
>;

type ManualTransactionFields = Pick<
    Transaction,
    "dateOnly" | "amount" | "bankDescription"
>;

export function buildUpdateManualTransactionFormSchema(lockDate: Date | null) {
    return manualTransactionUpdateFieldsSchema.refine(
        ({ dateOnly }) => !isLockedByDateOnly(lockDate, dateOnly),
        {
            message: "Pick a date after the locked period",
            path: ["dateOnly"],
        },
    );
}

export function toUpdateManualTransactionFormValues(
    transaction: ManualTransactionFields,
): UpdateManualTransactionFormValues {
    return {
        dateOnly: transaction.dateOnly ?? "",
        amount: transaction.amount,
        bankDescription: transaction.bankDescription,
    };
}

/** The request body with only the fields that differ from the transaction. */
export function toUpdateManualTransactionBody({
    transaction,
    values,
}: {
    transaction: ManualTransactionFields;
    values: UpdateManualTransactionFormValues;
}): UpdateManualTransactionBody {
    const bankDescription = values.bankDescription.trim();
    return {
        ...(values.dateOnly !== transaction.dateOnly && {
            dateOnly: values.dateOnly,
        }),
        ...(values.amount !== transaction.amount && {
            amount: values.amount,
        }),
        ...(bankDescription !== transaction.bankDescription && {
            bankDescription,
        }),
    };
}
